// External
import { ReactNode, useContext, useState, useRef } from 'react';
import { LoadingButton } from '@mui/lab';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Auth } from 'aws-amplify';
import CryptoJS from 'crypto-js';
import { v4 as uuidv4 } from 'uuid';
import ReCAPTCHA from 'react-google-recaptcha';

// Project
import PATHS from 'src/config/paths';
import { TextInput, PasswordInput, Checkbox } from 'src/components/Form';
import { useLocalAppUser } from 'src/hooks';
import { SIGN_IN_TERMS } from 'src/config/messages';
import { ExternalLink } from 'src/components/Link';
import { CSEA_CUSTOMER_SERVICE_URL, configs } from 'src/config';
import { Environment } from 'src/enums';

// Feature
import { CognitoContext } from '../../providers';

const DEFAULT_FORM_ERROR_STATE = {
  email: false,
  password: false,
};

export const CustomerSignIn = () => {
  const { appUser } = useParams();
  const navigate = useNavigate();
  const { setLocalAppUser } = useLocalAppUser();
  const { updateCognitoObj } = useContext(CognitoContext);

  const isDevEnv = configs.APP_ENV === Environment.DEV;
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [form, setForm] = useState({
    email: '',
    password: '',
    termsAndConditions: false,
  });
  const [formErrors, setFormErrors] = useState(DEFAULT_FORM_ERROR_STATE);
  const [error, setError] = useState<ReactNode>();
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(isDevEnv ? 'bypass' : null);

  // Block access if not coming from allowed referrers (Prod only)
  const allowedReferrers = [
    'https://ag.hawaii.gov/csea/',
    'https://ag.hawaii.gov/',
    'https://csea.ehawaii.gov/iwa/index.html',
  ];
  if (configs.APP_ENV === Environment.PROD && !allowedReferrers.includes(document.referrer)) {
    window.location.href = 'https://ag.hawaii.gov/csea/';
    return null;
  }

  const resetFormErrors = () => {
    setFormErrors(DEFAULT_FORM_ERROR_STATE);
    setError('');
  };

  const markFieldError = (field: string) => {
    setFormErrors((prev) => ({ ...prev, [field]: true }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    resetFormErrors();

    const { email, password } = form;
    try {
      const user = await Auth.signIn(email, password, {
        email,
        captchaToken: isDevEnv ? null : recaptchaToken,
      });

      if (isDevEnv) {
        const currentUser = await Auth.currentAuthenticatedUser();
        if (!currentUser.attributes.email_verified) {
          navigate(PATHS.verifyEmail);
        } else {
          // Successfully signed in
          setLocalAppUser(appUser === 'customer' ? 'customer' : 'agency');
          navigate(PATHS.linkKeiki);
          toast.info('MFA temporarily disabled.');
        }
      } else {
        // Staging environment and above
        updateCognitoObj(user);
        const key = uuidv4();
        const encryptedPassword = CryptoJS.AES.encrypt(password, key).toString();
        sessionStorage.setItem(`encrypted-code.${email}`, encryptedPassword);
        sessionStorage.setItem(`encrypt-key.${email}`, key);
        navigate(PATHS.smsMFA);
      }
    } catch (err: any) {
      console.log('SignInError', err);
      switch (err.code) {
        case 'NotAuthorizedException':
          // try {
          //   let response = await API.get('case', `/signin-lockout-status/${email}`, {}); // API from aws-amplify
          //   if (response.message.locked) {
          //     setError(<LockoutMessage />);
          //   } else {
          //     setError(
          //       'Incorrect email or password. After 3 unsuccessful sign in attempts, you will ' +
          //         'be locked out for a period of 15 minutes.'
          //     );
          //   }
          // } catch (statusErr) {
          //   console.log('GetLockoutStatusError', statusErr);
          //   setError('System error. Please try again later.');
          // }
          setError(
            'Incorrect email or password. After 3 unsuccessful sign in attempts, you will ' +
              'be locked out for a period of 15 minutes.'
          );
          markFieldError('email');
          markFieldError('password');
          break;
        case 'UserNotFoundException':
          setError('Email does not exist. Please try again.');
          markFieldError('email');
          break;
        case 'UserNotConfirmedException':
          navigate(PATHS.verifySMS, { state: { email } });
          break;
        case 'UserLambdaValidationException':
          const prefix = 'PreAuthentication failed with error ';
          if (err.message.startsWith(prefix)) {
            setError(<LockoutMessage />);
          } else {
            setError('System error. Please try again later.');
          }
          break;
        default:
          setError('System error. Please try again later.');
      }
      recaptchaRef.current?.reset();
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h1 className="text-center text-2xl">Sign In</h1>
      <form onSubmit={handleSignIn} id="sign-in-form">
        <TextInput
          autoComplete="off"
          name="email"
          label="Email"
          value={form.email}
          placeholder="Email"
          required
          onChange={handleChange}
          error={formErrors.email}
          className="mb-5"
        />
        <PasswordInput
          autoComplete="off"
          name="password"
          label="Password"
          value={form.password}
          placeholder="Password"
          required
          onChange={handleChange}
          error={formErrors.password}
        />
      </form>
      {error && <div className="my-1 text-red-600">{error}</div>}
      <div className="mt-1">
        <Link to={PATHS.forgotPassword(appUser)} className="text-primary no-underline">
          Forgot Password?
        </Link>
      </div>
      <Checkbox
        checked={form.termsAndConditions}
        onChange={handleChange}
        name="termsAndConditions"
        label="I agree with the Terms and Conditions."
      />
      <p className="m-0 text-sm">{SIGN_IN_TERMS}</p>
      {!isDevEnv && (
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={configs.RECAPTCHA_SITE_KEY}
          className="mt-4 flex justify-center"
          onChange={setRecaptchaToken}
        />
      )}
      <LoadingButton
        variant="contained"
        className="mt-4 w-full normal-case"
        form="sign-in-form"
        type="submit"
        loading={loading}
        disabled={!form.termsAndConditions || !recaptchaToken}
      >
        <span>Sign In</span>
      </LoadingButton>
      <div className="mt-5 flex justify-center">
        New to CSEA?&nbsp;
        <Link to={PATHS.pinCheck} className="text-primary no-underline">
          Create an Account
        </Link>
      </div>
    </>
  );
};

const LockoutMessage = () => (
  <>
    You have reached the maximum attempts to Sign In and will be locked out for a period of 15
    minutes. For questions or issues regarding how to sign in, please contact CSEA{' '}
    <ExternalLink text="Customer Service." href={CSEA_CUSTOMER_SERVICE_URL} />
  </>
);
